import { Calendar, CreditCard, Settings, Zap } from 'lucide-react';

import { useTranslation } from '@/components/i18n/Translator';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface SubscriptionData {
  plan: string;
  status: 'active' | 'pending' | 'cancelled' | 'expired' | 'trialing';
  nextBilling: string;
  amount: string;
  features?: string[];
}

interface SubscriptionCardProps {
  subscription: SubscriptionData;
  onManageSubscription?: () => void;
  onChangePayment?: () => void;
  onUpgrade?: () => void;
  isProUser?: boolean;
}

const SubscriptionCard = ({
  subscription,
  onManageSubscription,
  onChangePayment,
  onUpgrade,
  isProUser = false
}: SubscriptionCardProps) => {
  const { t } = useTranslation();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-600 text-white border-emerald-600';
      case 'trialing':
        return 'bg-blue-500 text-white border-blue-500';
      case 'pending':
        return 'bg-amber-500 text-white border-amber-500';
      case 'cancelled':
        return 'bg-red-500 text-white border-red-500';
      case 'expired':
        return 'bg-slate-500 text-white border-slate-500';
      default:
        return 'bg-slate-500 text-white border-slate-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Subscription Overview Grid */}
      <div className={`grid grid-cols-1 gap-4 ${isProUser ? 'sm:grid-cols-2 lg:grid-cols-4' : 'max-w-xs'}`}>
        <Card className="p-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Zap className="w-4 h-4" />
              <span>{t('subscription.card.currentPlan')}</span>
            </div>
            <div className="text-2xl font-bold">
              {subscription.plan === 'Free' || subscription.plan === 'free'
                ? t('subscription.card.plans.free')
                : subscription.plan}
            </div>
            {!isProUser && (
              <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-600">
                {t('subscription.card.plans.free')}
              </div>
            )}
          </div>
        </Card>

        {isProUser && (
          <>
            <Card className="p-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Settings className="w-4 h-4" />
                  <span>{t('subscription.card.status')}</span>
                </div>
                <div
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(
                    subscription.status
                  )}`}
                >
                  {t(`subscription.card.statusLabels.${subscription.status}`)}
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>{t('subscription.card.nextBilling')}</span>
                </div>
                <div className="text-lg font-semibold">
                  {subscription.nextBilling}
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <CreditCard className="w-4 h-4" />
                  <span>{t('subscription.card.amount')}</span>
                </div>
                <div className="text-lg font-semibold">{subscription.amount}</div>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        {isProUser ? (
          <>
            <div className="flex flex-col gap-1">
              <Button
                onClick={onManageSubscription}
                disabled={!onManageSubscription}
                variant="default"
                className="flex-1 sm:flex-none bg-foreground text-background hover:bg-foreground/90"
              >
                <Settings className="w-4 h-4 mr-2" />
                {t('subscription.card.actions.manageSubscription')}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t('subscription.card.actions.manageSubscriptionHint')}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={onChangePayment}
              disabled={!onChangePayment}
              className="flex-1 sm:flex-none"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {t('subscription.card.actions.changePayment')}
            </Button>
            {subscription.plan !== 'Enterprise' && (
              <Button
                variant="outline"
                onClick={onUpgrade}
                disabled={!onUpgrade}
                className="flex-1 sm:flex-none"
              >
                <Zap className="w-4 h-4 mr-2" />
                {t('subscription.card.actions.upgradePlan')}
              </Button>
            )}
          </>
        ) : (
          <Button
            onClick={onUpgrade}
            disabled={!onUpgrade}
            variant="default"
            className="flex-1 sm:flex-none bg-foreground text-background hover:bg-foreground/90"
          >
            <Zap className="w-4 h-4 mr-2" />
            {t('subscription.card.actions.upgradePlan')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCard;
